import {isNotNil} from 'ramda';

export interface TextEditorRectangle {
  top: number;
  left: number;
  width: number;
  height: number;
}

const LINE_TOLERANCE = 1;

const isSameLine = (a: TextEditorRectangle, b: TextEditorRectangle) =>
  Math.abs(a.top - b.top) <= LINE_TOLERANCE && Math.abs(a.height - b.height) <= LINE_TOLERANCE;

/**
 * Joins rectangles that touch or overlap on one line into a single box. Adjacent word ranges and
 * a wrapped range's client rects otherwise stack their translucent fills into darker blotches.
 */
export function mergeTextEditorRectangles(rects: TextEditorRectangle[]): TextEditorRectangle[] {
  const sorted = [...rects].sort((a, b) => a.top - b.top || a.left - b.left);

  return sorted.reduce<TextEditorRectangle[]>((merged, rect) => {
    const last = merged[merged.length - 1];

    if (
      isNotNil(last) &&
      isSameLine(last, rect) &&
      rect.left <= last.left + last.width + LINE_TOLERANCE
    ) {
      const right = Math.max(last.left + last.width, rect.left + rect.width);
      const top = Math.min(last.top, rect.top);
      const bottom = Math.max(last.top + last.height, rect.top + rect.height);

      return [
        ...merged.slice(0, -1),
        {top, left: last.left, width: right - last.left, height: bottom - top},
      ];
    }

    return [...merged, rect];
  }, []);
}
